import { useState } from 'react'

import { addDays, format } from 'date-fns'
import { DataItem } from 'scrivito'

import { buildEmployeeData } from './buildEmployeeData'
import { buildEmployeesFromFetched } from './loadEmployeesForIds'
import { MY_TEAM_CONSTANTS } from '../../const'
import { useTeamMembers } from '../../hooks/useTeamMembers'
import { FilterValues, WeekRange } from '../../types'
import { weekRangeToApiFilters } from '../../utils/dateUtils'
import { Timerecord } from '@/Data/Timerecord/TimerecordDataClass'
import { TimerecordUtilization } from '@/Data/TimerecordUtilization/TimerecordUtilizationDataClass'

export { buildEmployeeData }

const { PAGE_SIZE } = MY_TEAM_CONSTANTS

export const fetchUserRecords = (
  userId: string,
  dateFilters: Record<string, string>,
): DataItem[] =>
  Timerecord.all()
    .transform({
      filters: { user: userId, ...dateFilters },
      order: [['recordedAt', 'desc']],
      limit: '',
    } as object)
    .take()

export const fetchUserUtilization = (
  userId: string,
  monthRange: WeekRange,
): DataItem[] =>
  TimerecordUtilization.all()
    .transform({
      filters: {
        user: userId,
        'date.gte': `${monthRange.startDate}T00:00:00Z`,
        'date.lt': `${format(addDays(new Date(monthRange.endDate), 1), 'yyyy-MM-dd')}T00:00:00Z`,
      },
      limit: '',
    } as object)
    .take()

export const useMyTeamData = (
  monthRange: WeekRange,
  currentUserId: string,
  appliedFilters?: FilterValues,
) => {
  const [limit, setLimit] = useState<number>(PAGE_SIZE)

  const leaderId = appliedFilters?.team || currentUserId
  const { members, totalMembers, hasMore } = useTeamMembers(leaderId, limit)
  const memberIds = members.map((m) => m.value)
  const dateFilters = weekRangeToApiFilters(monthRange)

  const { employeeMap, utilizationMap } = memberIds.reduce(
    (acc, id) => {
      acc.employeeMap.set(id, fetchUserRecords(id, dateFilters))
      acc.utilizationMap.set(id, fetchUserUtilization(id, monthRange))
      return acc
    },
    {
      employeeMap: new Map<string, DataItem[]>(),
      utilizationMap: new Map<string, DataItem[]>(),
    },
  )

  const totalRecords = [...employeeMap.values()].reduce(
    (sum, recs) => sum + recs.length,
    0,
  )

  const employees = buildEmployeesFromFetched(
    memberIds.map((id) => ({
      id,
      records: employeeMap.get(id) ?? [],
      utilization: utilizationMap.get(id) ?? [],
    })),
  )

  return {
    employees,
    totalEmployees: totalMembers,
    totalRecords,
    hasMore,
    loadMore: () => setLimit((prev) => prev + PAGE_SIZE),
  }
}
